import React, { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from './api';
import { EstadoError } from './EstadoError';

const DIAS = [
  { id: 1, nombre: 'Lun' }, { id: 2, nombre: 'Mar' }, { id: 3, nombre: 'Mié' },
  { id: 4, nombre: 'Jue' }, { id: 5, nombre: 'Vie' }, { id: 6, nombre: 'Sáb' },
  { id: 0, nombre: 'Dom' },
];

const FORM_INICIAL = {
  id: null,
  nombre: '',
  descripcion: '',
  tipo: 'descuento_producto',
  alcance: 'productos',
  descuento_porcentaje: '',
  descuento_monto: '',
  precio_combo: '',
  cantidad_lleva: 3,
  cantidad_paga: 2,
  porcentaje_segunda: 50,
  fecha_inicio: '',
  fecha_fin: '',
  hora_inicio: '',
  hora_fin: '',
  dias_semana: [0, 1, 2, 3, 4, 5, 6],
  prioridad: 0,
  acumulable: false,
  margen_minimo_porcentaje: 0,
};

const tipoEtiqueta = (p) => {
  if (p.tipo === '2x1') return '2x1';
  if (p.tipo === 'nxm') return `Llevá ${Number(p.cantidad_lleva || 0)}, pagá ${Number(p.cantidad_paga || 0)}`;
  if (p.tipo === 'segunda_unidad') return `${Number(p.porcentaje_segunda || 0)}% en 2.ª unidad`;
  if (p.tipo === 'combo_fijo') return `Combo $${Number(p.precio_combo || 0).toFixed(2)}`;
  if (Number(p.descuento_porcentaje || 0) > 0) return `${Number(p.descuento_porcentaje).toFixed(2)}% descuento`;
  return `$${Number(p.descuento_monto || 0).toFixed(2)} por unidad`;
};

const moneda = (valor) => Number(valor || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });

export default function Promociones() {
  const [pestaña, setPestaña] = useState('configuracion');
  const [promociones, setPromociones] = useState([]);
  const [productos, setProductos] = useState([]);
  const [errorCarga, setErrorCarga] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState(FORM_INICIAL);
  const [itemsSeleccionados, setItemsSeleccionados] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const hoy = new Date().toISOString().slice(0, 10);
  const inicioMes = `${hoy.slice(0, 8)}01`;
  const [filtroReporte, setFiltroReporte] = useState({ inicio: inicioMes, fin: hoy });
  const [reporte, setReporte] = useState([]);
  const [cargandoReporte, setCargandoReporte] = useState(false);

  const notificarPromocionesActualizadas = () => window.dispatchEvent(new CustomEvent('promociones-actualizadas'));

  const cargarDatos = async () => {
    setErrorCarga('');
    try {
      const [dataPromos, dataProductos] = await Promise.all([
        api.get('/api/promociones'),
        api.get('/api/productos'),
      ]);
      setPromociones(Array.isArray(dataPromos) ? dataPromos : []);
      setProductos(Array.isArray(dataProductos) ? dataProductos : []);
    } catch (error) {
      setErrorCarga(error instanceof ApiError ? error.mensaje : 'No se pudieron cargar las promociones.');
    }
  };

  useEffect(() => { cargarDatos(); }, []);

  const categorias = useMemo(() => [...new Set(productos.map((p) => p.categoria).filter(Boolean))].sort(), [productos]);
  const marcas = useMemo(() => [...new Set(productos.map((p) => p.marca).filter(Boolean))].sort(), [productos]);
  const productosFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return productos.filter((p) => !q
      || String(p.descripcion || '').toLowerCase().includes(q)
      || String(p.codigo_barras || '').includes(q)
      || String(p.marca || '').toLowerCase().includes(q));
  }, [productos, busqueda]);

  const cambiarTipo = (tipo) => {
    let alcance = form.alcance;
    if (tipo === 'descuento_categoria') alcance = 'categorias';
    if (tipo === 'descuento_marca') alcance = 'marcas';
    if (['2x1', 'nxm', 'segunda_unidad', 'combo_fijo'].includes(tipo)) alcance = 'productos';
    setForm((actual) => ({ ...actual, tipo, alcance }));
    setItemsSeleccionados([]);
  };

  const cambiarAlcance = (alcance) => {
    let tipo = form.tipo;
    if (alcance === 'categorias' && tipo.startsWith('descuento_')) tipo = 'descuento_categoria';
    if (alcance === 'marcas' && tipo.startsWith('descuento_')) tipo = 'descuento_marca';
    if (alcance === 'productos' && ['descuento_categoria', 'descuento_marca'].includes(tipo)) tipo = 'descuento_producto';
    setForm((actual) => ({ ...actual, alcance, tipo }));
    setItemsSeleccionados([]);
  };

  const alternarProducto = (producto) => {
    setItemsSeleccionados((actuales) => {
      const existe = actuales.some((item) => Number(item.producto_id) === Number(producto.id));
      if (existe) return actuales.filter((item) => Number(item.producto_id) !== Number(producto.id));
      return [...actuales, { producto_id: producto.id, cantidad_minima: 1 }];
    });
  };

  const alternarValor = (tipo, valor) => {
    setItemsSeleccionados((actuales) => {
      const existe = actuales.some((item) => item[tipo] === valor);
      if (existe) return actuales.filter((item) => item[tipo] !== valor);
      return [...actuales, { [tipo]: valor, cantidad_minima: 1 }];
    });
  };

  const cambiarCantidad = (clave, valor) => {
    const cantidad = Math.max(0.001, Number.parseFloat(valor) || 1);
    setItemsSeleccionados((actuales) => actuales.map((item) => {
      const coincide = clave.tipo === 'producto_id'
        ? Number(item.producto_id) === Number(clave.valor)
        : item[clave.tipo] === clave.valor;
      return coincide ? { ...item, cantidad_minima: cantidad } : item;
    }));
  };

  const alternarDia = (dia) => {
    setForm((actual) => ({
      ...actual,
      dias_semana: actual.dias_semana.includes(dia)
        ? actual.dias_semana.filter((d) => d !== dia)
        : [...actual.dias_semana, dia],
    }));
  };

  const limpiarFormulario = () => {
    setForm(FORM_INICIAL);
    setItemsSeleccionados([]);
    setBusqueda('');
  };

  const editar = (promo) => {
    setPestaña('configuracion');
    setForm({
      ...FORM_INICIAL,
      ...promo,
      fecha_inicio: promo.fecha_inicio ? String(promo.fecha_inicio).slice(0, 10) : '',
      fecha_fin: promo.fecha_fin ? String(promo.fecha_fin).slice(0, 10) : '',
      hora_inicio: promo.hora_inicio ? String(promo.hora_inicio).slice(0, 5) : '',
      hora_fin: promo.hora_fin ? String(promo.hora_fin).slice(0, 5) : '',
      dias_semana: Array.isArray(promo.dias_semana) ? promo.dias_semana.map(Number) : [0, 1, 2, 3, 4, 5, 6],
    });
    setItemsSeleccionados(Array.isArray(promo.items) ? promo.items.map((item) => ({
      producto_id: item.producto_id ? Number(item.producto_id) : null,
      categoria: item.categoria || null,
      marca: item.marca || null,
      cantidad_minima: Number(item.cantidad_minima || 1),
    })) : []);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const guardar = async (evento) => {
    evento.preventDefault();
    if (!form.nombre.trim()) return alert('Ingresá un nombre para la promoción.');
    if (!itemsSeleccionados.length) return alert('Seleccioná al menos un producto, categoría o marca.');
    if (!form.dias_semana.length) return alert('Seleccioná al menos un día de vigencia.');
    setGuardando(true);
    try {
      const payload = { ...form, items: itemsSeleccionados };
      if (form.id) await api.put(`/api/promociones/${form.id}`, payload);
      else await api.post('/api/promociones', payload);
      alert(form.id ? '✅ Promoción actualizada.' : '✅ Promoción creada y activada.');
      limpiarFormulario();
      await cargarDatos();
      notificarPromocionesActualizadas();
    } catch (error) {
      alert(`❌ ${error instanceof ApiError ? error.mensaje : 'No se pudo guardar la promoción.'}`);
    } finally { setGuardando(false); }
  };

  const cambiarEstado = async (promo) => {
    try {
      await api.patch(`/api/promociones/${promo.id}/estado`, { activo: !promo.activo });
      await cargarDatos();
      notificarPromocionesActualizadas();
    } catch (error) { alert(`❌ ${error instanceof ApiError ? error.mensaje : 'No se pudo cambiar el estado.'}`); }
  };

  const duplicar = async (promo) => {
    try {
      await api.post(`/api/promociones/${promo.id}/duplicar`, {});
      await cargarDatos();
      alert('✅ Se creó una copia pausada para que puedas editarla.');
    } catch (error) { alert(`❌ ${error instanceof ApiError ? error.mensaje : 'No se pudo duplicar.'}`); }
  };

  const eliminar = async (promo) => {
    if (!window.confirm(`¿Eliminar definitivamente “${promo.nombre}”?`)) return;
    try {
      await api.delete(`/api/promociones/${promo.id}`);
      await cargarDatos();
      notificarPromocionesActualizadas();
    } catch (error) { alert(`❌ ${error instanceof ApiError ? error.mensaje : 'No se pudo eliminar.'}`); }
  };

  const cargarReporte = async () => {
    setCargandoReporte(true);
    try {
      const data = await api.get(`/api/promociones/reporte?inicio=${filtroReporte.inicio}&fin=${filtroReporte.fin}`);
      setReporte(Array.isArray(data?.promociones) ? data.promociones : []);
    } catch (error) { alert(`❌ ${error instanceof ApiError ? error.mensaje : 'No se pudo generar el reporte.'}`); }
    finally { setCargandoReporte(false); }
  };

  if (errorCarga) return <EstadoError mensaje={errorCarga} onReintentar={cargarDatos} />;

  const renderSelector = () => {
    if (form.alcance === 'categorias') {
      return <SelectorValores valores={categorias} tipo="categoria" seleccionados={itemsSeleccionados} alternar={alternarValor} cambiarCantidad={cambiarCantidad} vacio="No hay categorías cargadas." />;
    }
    if (form.alcance === 'marcas') {
      return <SelectorValores valores={marcas} tipo="marca" seleccionados={itemsSeleccionados} alternar={alternarValor} cambiarCantidad={cambiarCantidad} vacio="Cargá la marca en los productos para usar esta opción." />;
    }
    return (
      <>
        <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar por producto, código o marca…" style={{ ...inputStyle, marginBottom: 8 }} />
        <div style={selectorStyle}>
          {productosFiltrados.map((producto) => {
            const seleccionado = itemsSeleccionados.find((item) => Number(item.producto_id) === Number(producto.id));
            return <div key={producto.id} style={{ ...filaSelector, background: seleccionado ? '#ecfdf5' : 'white' }}>
              <input type="checkbox" checked={Boolean(seleccionado)} onChange={() => alternarProducto(producto)} />
              <div style={{ flex: 1 }}>
                <strong>{producto.descripcion}</strong>
                <small style={{ display: 'block', color: '#64748b' }}>{producto.categoria || 'General'}{producto.marca ? ` · ${producto.marca}` : ''}</small>
              </div>
              {seleccionado && <input aria-label={`Cantidad mínima ${producto.descripcion}`} type="number" min="0.001" step={producto.acepta_fraccion ? '0.001' : '1'} value={seleccionado.cantidad_minima} onChange={(e) => cambiarCantidad({ tipo: 'producto_id', valor: producto.id }, e.target.value)} style={cantidadStyle} />}
              <span>{moneda(producto.precio_venta)}</span>
            </div>;
          })}
        </div>
      </>
    );
  };

  return (
    <div style={{ padding: 20, background: '#f8fafc', minHeight: '82vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <div><h2 style={{ margin: 0, color: '#0f172a' }}>Promociones automáticas</h2><p style={{ margin: '5px 0 0', color: '#64748b' }}>Definí ofertas, horarios, prioridad y margen protegido.</p></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setPestaña('configuracion')} style={pestaña === 'configuracion' ? tabActivo : tab}>⚙️ Configuración</button>
          <button onClick={() => { setPestaña('reporte'); cargarReporte(); }} style={pestaña === 'reporte' ? tabActivo : tab}>📊 Resultados</button>
        </div>
      </div>

      {pestaña === 'reporte' ? (
        <ReportePromociones filtro={filtroReporte} setFiltro={setFiltroReporte} cargar={cargarReporte} cargando={cargandoReporte} filas={reporte} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(360px, 1.15fr) minmax(320px, .85fr)', gap: 18 }}>
          <form onSubmit={guardar} style={panelStyle}>
            <h3 style={{ marginTop: 0 }}>{form.id ? `Editar promoción #${form.id}` : 'Nueva promoción'}</h3>
            <label style={labelStyle}>Nombre</label>
            <input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} style={inputStyle} placeholder="Ej.: 3x2 en gaseosas" required />
            <label style={labelStyle}>Descripción interna</label>
            <textarea value={form.descripcion || ''} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} style={{ ...inputStyle, minHeight: 60 }} />

            <div style={grid2}>
              <div><label style={labelStyle}>Mecánica</label><select aria-label="Mecánica" value={form.tipo} onChange={(e) => cambiarTipo(e.target.value)} style={inputStyle}>
                <option value="descuento_producto">Descuento directo</option>
                <option value="2x1">2x1</option>
                <option value="nxm">Llevá N, pagá M</option>
                <option value="segunda_unidad">Segunda unidad con descuento</option>
                <option value="combo_fijo">Combo a precio fijo</option>
              </select></div>
              <div><label style={labelStyle}>Aplicar por</label><select disabled={form.tipo === 'combo_fijo'} value={form.alcance} onChange={(e) => cambiarAlcance(e.target.value)} style={inputStyle}>
                <option value="productos">Productos</option>
                {!['2x1', 'nxm', 'segunda_unidad', 'combo_fijo'].includes(form.tipo) && <option value="categorias">Categorías</option>}
                {!['2x1', 'nxm', 'segunda_unidad', 'combo_fijo'].includes(form.tipo) && <option value="marcas">Marcas</option>}
              </select></div>
            </div>

            {form.tipo.startsWith('descuento_') && <div style={grid2}>
              <div><label style={labelStyle}>Descuento (%)</label><input type="number" min="0" max="100" step="0.01" value={form.descuento_porcentaje} onChange={(e) => setForm({ ...form, descuento_porcentaje: e.target.value, descuento_monto: '' })} style={inputStyle} /></div>
              <div><label style={labelStyle}>o monto por unidad ($)</label><input type="number" min="0" step="0.01" value={form.descuento_monto} onChange={(e) => setForm({ ...form, descuento_monto: e.target.value, descuento_porcentaje: '' })} style={inputStyle} /></div>
            </div>}
            {form.tipo === 'nxm' && <div style={grid2}><div><label style={labelStyle}>Cantidad que lleva (N)</label><input type="number" min="2" step="1" value={form.cantidad_lleva} onChange={(e) => setForm({ ...form, cantidad_lleva: e.target.value })} style={inputStyle} /></div><div><label style={labelStyle}>Cantidad que paga (M)</label><input type="number" min="1" step="1" value={form.cantidad_paga} onChange={(e) => setForm({ ...form, cantidad_paga: e.target.value })} style={inputStyle} /></div></div>}
            {form.tipo === 'segunda_unidad' && <div><label style={labelStyle}>Descuento en la segunda unidad (%)</label><input type="number" min="1" max="100" value={form.porcentaje_segunda} onChange={(e) => setForm({ ...form, porcentaje_segunda: e.target.value })} style={inputStyle} /></div>}
            {form.tipo === 'combo_fijo' && <div><label style={labelStyle}>Precio final del combo</label><input type="number" min="0.01" step="0.01" value={form.precio_combo} onChange={(e) => setForm({ ...form, precio_combo: e.target.value })} style={inputStyle} /></div>}

            <label style={labelStyle}>Alcance y cantidades mínimas</label>
            {renderSelector()}
            <small style={{ color: '#64748b' }}>{itemsSeleccionados.length} selección(es).</small>

            <h4 style={subtitulo}>Vigencia</h4>
            <div style={grid2}><div><label style={labelStyle}>Desde</label><input type="date" value={form.fecha_inicio || ''} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} style={inputStyle} /></div><div><label style={labelStyle}>Hasta</label><input type="date" value={form.fecha_fin || ''} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} style={inputStyle} /></div></div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '10px 0' }}>{DIAS.map((dia) => <button type="button" key={dia.id} onClick={() => alternarDia(dia.id)} style={form.dias_semana.includes(dia.id) ? diaActivo : diaInactivo}>{dia.nombre}</button>)}</div>
            <div style={grid2}><div><label style={labelStyle}>Hora inicial</label><input type="time" value={form.hora_inicio || ''} onChange={(e) => setForm({ ...form, hora_inicio: e.target.value })} style={inputStyle} /></div><div><label style={labelStyle}>Hora final</label><input type="time" value={form.hora_fin || ''} onChange={(e) => setForm({ ...form, hora_fin: e.target.value })} style={inputStyle} /></div></div>

            <h4 style={subtitulo}>Reglas de negocio</h4>
            <div style={grid2}><div><label style={labelStyle}>Prioridad</label><input type="number" value={form.prioridad} onChange={(e) => setForm({ ...form, prioridad: e.target.value })} style={inputStyle} /></div><div><label style={labelStyle}>Margen mínimo protegido (%)</label><input type="number" min="0" step="0.1" value={form.margen_minimo_porcentaje} onChange={(e) => setForm({ ...form, margen_minimo_porcentaje: e.target.value })} style={inputStyle} /></div></div>
            <label style={{ ...labelStyle, display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={Boolean(form.acumulable)} onChange={(e) => setForm({ ...form, acumulable: e.target.checked })} /> Permitir acumular con otras promociones también acumulables</label>

            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              {form.id && <button type="button" onClick={limpiarFormulario} style={botonSecundario}>Cancelar edición</button>}
              <button disabled={guardando} type="submit" style={botonPrimario}>{guardando ? 'Guardando…' : form.id ? 'Guardar cambios' : 'Crear y activar'}</button>
            </div>
          </form>

          <div style={panelStyle}>
            <h3 style={{ marginTop: 0 }}>Promociones configuradas</h3>
            {!promociones.length && <p style={{ color: '#94a3b8' }}>Todavía no hay promociones.</p>}
            {promociones.map((promo) => <TarjetaPromocion key={promo.id} promo={promo} editar={editar} cambiarEstado={cambiarEstado} duplicar={duplicar} eliminar={eliminar} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function SelectorValores({ valores, tipo, seleccionados, alternar, cambiarCantidad, vacio }) {
  if (!valores.length) return <div style={{ ...selectorStyle, color: '#94a3b8' }}>{vacio}</div>;
  return <div style={selectorStyle}>{valores.map((valor) => {
    const seleccionado = seleccionados.find((item) => item[tipo] === valor);
    return <div key={valor} style={{ ...filaSelector, background: seleccionado ? '#ecfdf5' : 'white' }}><input type="checkbox" checked={Boolean(seleccionado)} onChange={() => alternar(tipo, valor)} /><strong style={{ flex: 1 }}>{valor}</strong>{seleccionado && <input aria-label={`Cantidad mínima ${valor}`} type="number" min="0.001" step="1" value={seleccionado.cantidad_minima} onChange={(e) => cambiarCantidad({ tipo, valor }, e.target.value)} style={cantidadStyle} />}</div>;
  })}</div>;
}

function TarjetaPromocion({ promo, editar, cambiarEstado, duplicar, eliminar }) {
  const objetivos = (promo.items || []).map((item) => item.producto_nombre || item.categoria || item.marca).filter(Boolean).join(', ');
  const dias = Array.isArray(promo.dias_semana) ? DIAS.filter((d) => promo.dias_semana.map(Number).includes(d.id)).map((d) => d.nombre).join(' ') : 'Todos';
  return <div style={{ border: '1px solid #e2e8f0', borderLeft: `5px solid ${promo.activo ? '#10b981' : '#94a3b8'}`, borderRadius: 8, padding: 14, marginBottom: 12, background: promo.activo ? '#fff' : '#f8fafc' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><div><strong style={{ fontSize: 16 }}>{promo.nombre}</strong><div style={{ color: '#2563eb', fontWeight: 700, marginTop: 4 }}>{tipoEtiqueta(promo)}</div></div><span style={{ fontSize: 12, color: '#64748b' }}>Prioridad {promo.prioridad || 0}</span></div>
    <div style={{ fontSize: 12, color: '#475569', marginTop: 8 }}><b>Aplica a:</b> {objetivos || 'Sin objetivos'}<br/><b>Días:</b> {dias}{promo.hora_inicio && promo.hora_fin ? ` · ${String(promo.hora_inicio).slice(0,5)} a ${String(promo.hora_fin).slice(0,5)}` : ''}<br/><b>Margen protegido:</b> {Number(promo.margen_minimo_porcentaje || 0)}% · {promo.acumulable ? 'Acumulable' : 'No acumulable'}</div>
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}><button onClick={() => editar(promo)} style={miniBoton}>Editar</button><button onClick={() => duplicar(promo)} style={miniBoton}>Duplicar</button><button onClick={() => cambiarEstado(promo)} style={{ ...miniBoton, color: promo.activo ? '#b45309' : '#047857' }}>{promo.activo ? 'Pausar' : 'Activar'}</button><button onClick={() => eliminar(promo)} style={{ ...miniBoton, color: '#dc2626' }}>Eliminar</button></div>
  </div>;
}

function ReportePromociones({ filtro, setFiltro, cargar, cargando, filas }) {
  const totales = filas.reduce((acc, fila) => ({ tickets: acc.tickets + Number(fila.tickets || 0), descuento: acc.descuento + Number(fila.descuento_total || 0), venta: acc.venta + Number(fila.venta_neta || 0), margen: acc.margen + Number(fila.margen_bruto || 0) }), { tickets: 0, descuento: 0, venta: 0, margen: 0 });
  return <div style={panelStyle}>
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end' }}><div><label style={labelStyle}>Desde</label><input type="date" value={filtro.inicio} onChange={(e) => setFiltro({ ...filtro, inicio: e.target.value })} style={inputStyle} /></div><div><label style={labelStyle}>Hasta</label><input type="date" value={filtro.fin} onChange={(e) => setFiltro({ ...filtro, fin: e.target.value })} style={inputStyle} /></div><button onClick={cargar} style={botonPrimario}>{cargando ? 'Calculando…' : 'Actualizar reporte'}</button></div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(140px,1fr))', gap: 10, margin: '18px 0' }}><Kpi titulo="Tickets" valor={totales.tickets} /><Kpi titulo="Ahorro otorgado" valor={moneda(totales.descuento)} /><Kpi titulo="Venta con promo" valor={moneda(totales.venta)} /><Kpi titulo="Margen bruto" valor={moneda(totales.margen)} /></div>
    <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', borderCollapse: 'collapse' }}><thead><tr>{['Promoción','Tipo','Tickets','Descuento','Venta neta','Costo','Margen','Margen %'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead><tbody>{filas.map((fila, i) => <tr key={`${fila.promocion_id}-${i}`}><td style={td}>{fila.promocion_nombre}</td><td style={td}>{fila.promocion_tipo}</td><td style={tdNum}>{fila.tickets}</td><td style={tdNum}>{moneda(fila.descuento_total)}</td><td style={tdNum}>{moneda(fila.venta_neta)}</td><td style={tdNum}>{moneda(fila.costo_asociado)}</td><td style={tdNum}>{moneda(fila.margen_bruto)}</td><td style={tdNum}>{Number(fila.margen_porcentaje || 0).toFixed(2)}%</td></tr>)}{!filas.length && <tr><td colSpan="8" style={{ ...td, textAlign: 'center', color: '#94a3b8' }}>No hay ventas con promociones en el período.</td></tr>}</tbody></table></div>
  </div>;
}

function Kpi({ titulo, valor }) { return <div style={{ padding: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}><small style={{ color: '#64748b' }}>{titulo}</small><strong style={{ display: 'block', marginTop: 4, fontSize: 18 }}>{valor}</strong></div>; }

const panelStyle = { background: 'white', padding: 22, borderRadius: 12, boxShadow: '0 3px 12px rgba(15,23,42,.06)' };
const labelStyle = { display: 'block', fontWeight: 700, margin: '10px 0 5px', color: '#475569', fontSize: 13 };
const inputStyle = { padding: 10, borderRadius: 7, border: '1px solid #cbd5e1', width: '100%', boxSizing: 'border-box', background: 'white' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };
const selectorStyle = { maxHeight: 230, overflowY: 'auto', border: '1px solid #cbd5e1', borderRadius: 8, padding: 8, background: '#f8fafc' };
const filaSelector = { display: 'flex', alignItems: 'center', gap: 9, padding: 8, borderRadius: 6, marginBottom: 5 };
const cantidadStyle = { width: 68, padding: 6, border: '1px solid #cbd5e1', borderRadius: 5 };
const botonPrimario = { padding: '10px 16px', background: '#2563eb', color: 'white', border: 0, borderRadius: 7, fontWeight: 700, cursor: 'pointer' };
const botonSecundario = { padding: '10px 16px', background: '#e2e8f0', color: '#334155', border: 0, borderRadius: 7, fontWeight: 700, cursor: 'pointer' };
const miniBoton = { padding: '6px 9px', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer', fontWeight: 700 };
const tab = { padding: '9px 13px', borderRadius: 8, border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer' };
const tabActivo = { ...tab, background: '#2563eb', color: 'white', border: '1px solid #2563eb' };
const diaInactivo = { border: '1px solid #cbd5e1', borderRadius: 18, background: 'white', padding: '6px 10px', cursor: 'pointer' };
const diaActivo = { ...diaInactivo, background: '#dbeafe', borderColor: '#60a5fa', color: '#1d4ed8', fontWeight: 700 };
const subtitulo = { margin: '18px 0 5px', color: '#0f172a', borderBottom: '1px solid #e2e8f0', paddingBottom: 5 };
const th = { textAlign: 'left', padding: 9, borderBottom: '2px solid #cbd5e1', color: '#475569', fontSize: 12 };
const td = { padding: 9, borderBottom: '1px solid #e2e8f0', fontSize: 13 };
const tdNum = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };
